import { DATE_FORMAT_STRING, TDate, TTimelineDateRangeDTO } from '@monorepo/types';
import moment from 'moment';

export const DISPLAY_PIPE_DATE_FORMAT = 'dd-MM-yyyy';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export class DateUtils {
  //example: Tue 19 Dec 2023
  static fullDateFormat() {
    const day = moment();
    return `${day.format('ddd')} ${day.format('DD')} ${day.format('MMM')} ${day.format('YYYY')}`;
  }

  //example 13:45 -> 1:45pm
  static getTimeformatte(time: string) {
    const parsedTime = moment(time, 'HH:mm');
    const formattedTime = parsedTime.format('h:mma');
    return formattedTime;
  }

  // get current date //
  static getCrntDate() {
    const day = moment();
    return day.format('YYYY-MM-DD');
  }

  //Here datestring is of format YYYY-MM-DD as sent by backend
  static getDateFromDateString = (dateString: string) => {
    return new Date(dateString);
  };

  //Here datestring is of format YYYY-MM-DD as sent by backend
  static getDateStringFromDate = (date: number | Date | string): TDate => {
    return moment(date).format('YYYY-MM-DD') as TDate;
  };

  static getDisplayDateStringFromDate = (date: number | Date | string) => {
    return moment(date).format('DD-MM-YYYY');
  };

  static getFirstDateOfMonthFromDate = (date: Date): TDate => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return this.getDateStringFromDate(new Date(year, month).valueOf());
  };

  static getLastDateOfMonthFromDate = (date: number | Date): TDate => {
    const date$ = new Date(date);
    const year = date$.getFullYear();
    const month = date$.getMonth();
    return this.getDateStringFromDate(new Date(year, month + 1, 0).valueOf());
  };

  //takes two YYYY-MM-DD strings and returns one between them YYYY-MM-DD
  static generateRandomDate = (startDate: string, endDate: string): string => {
    const firstDate = new Date(startDate);
    const lastDate = new Date(endDate);
    const timeDiff = lastDate.getTime() - firstDate.getTime();
    const randomTime = Math.random() * timeDiff;
    const randomDate = new Date(firstDate.getTime() + randomTime);
    return DateUtils.getDateStringFromDate(randomDate);
  };

  /**
   * Checks whether a given date string falls within the past N days (inclusive).
   *
   * @param date - The input date string (ISO format or any format parsable by Moment).
   * @param numOfDays - The number of days to look back from today.
   * @returns `true` if the date is within the past `numOfDays` (including today), `false` otherwise.
   *
   * @example
   * isDateWithinPastDays('2025-07-01', 3) // returns true if today is between July 1 and July 3, 2025
   */
  static isDateWithinPastDays(date: string, numOfDays: number): boolean {
    const today = moment();
    const selected = moment.parseZone(date);
    const fromDate = today.clone().subtract(numOfDays, 'days');

    return selected.isSameOrAfter(fromDate, 'day') && selected.isSameOrBefore(today, 'day');
  }

  /**
   * Fits a requested date range within an available date range, preserving the requested span whenever possible.
   *
   * @param requested - The desired date range.
   * @param available - The date range that the result must fit within.
   * @param anchorDate - Optional date that the returned range should include when the requested range has no overlap with the available range. Defaults to today.
   *
   * Rules:
   * - If the requested range already fits entirely within the available range,
   *   it is returned unchanged.
   * - If the requested range is wider than the available range, the available
   *   range is returned.
   * - If the requested range partially overlaps the available range, the overlap
   *   is preserved and the range is extended on the clipped side to maintain the
   *   original width.
   * - If there is no overlap:
   *   - If `anchorDate` (or today when omitted) lies within the available range,
   *     the returned range is anchored at that date while preserving the original
   *     width. If necessary, it is shifted so it remains entirely within the
   *     available range while still containing the anchor date.
   *   - Otherwise, the returned range starts from the beginning of the available
   *     range.
   *
   * All dates are required to be in YYYY-MM-DD format and are treated as whole days (time components are ignored).
   *
   * @example
   * ```ts
   * fitDateRange(
   *   { from: '2026-01-05', to: '2026-01-10' },
   *   { from: '2026-01-01', to: '2026-01-31' }
   * )
   * // => { from: '2026-01-05', to: '2026-01-10' }
   * ```
   *
   * @example
   * ```ts
   * fitDateRange(
   *   { from: '2026-01-01', to: '2026-01-15' },
   *   { from: '2026-01-10', to: '2026-01-31' }
   * )
   * // => { from: '2026-01-10', to: '2026-01-24' }
   *```
   *
   * @example
   * ```ts
   * fitDateRange(
   *   { from: '2026-03-01', to: '2026-03-10' },
   *   { from: '2026-01-01', to: '2026-01-31' },
   *   '2026-01-28'
   * )
   * // => { from: '2026-01-22', to: '2026-01-31' }
   * ```
   */
  static fitDateRange(
    requested: TTimelineDateRangeDTO,
    available: TTimelineDateRangeDTO,
    anchorDate?: string,
  ): TTimelineDateRangeDTO {
    const requestedWidth = (Date.parse(requested.to) - Date.parse(requested.from)) / MS_PER_DAY + 1;
    const availableWidth = (Date.parse(available.to) - Date.parse(available.from)) / MS_PER_DAY + 1;

    // Available range is narrower than requested.
    if (availableWidth <= requestedWidth) return available;

    // Requested already fits completely.
    if (requested.from >= available.from && requested.to <= available.to) return requested;

    const hasOverlap = requested.from <= available.to && requested.to >= available.from;

    // --------------------------------------------------------------------------
    // Partial overlap.
    // --------------------------------------------------------------------------
    if (hasOverlap) {
      let from = moment(requested.from > available.from ? requested.from : available.from, DATE_FORMAT_STRING).startOf(
        'day',
      );

      let to = moment(requested.to < available.to ? requested.to : available.to, DATE_FORMAT_STRING).startOf('day');

      const overlapWidth = to.diff(from, 'days') + 1;
      const remaining = requestedWidth - overlapWidth;

      if (requested.from < available.from) {
        // Left side was clipped -> extend right.
        to.add(remaining, 'days');
      } else {
        // Right side was clipped -> extend left.
        from.subtract(remaining, 'days');
      }

      return { from: from.format(DATE_FORMAT_STRING), to: to.format(DATE_FORMAT_STRING) };
    }

    // --------------------------------------------------------------------------
    // No overlap.
    // --------------------------------------------------------------------------
    const availableFrom = moment(available.from, DATE_FORMAT_STRING).startOf('day');
    const availableTo = moment(available.to, DATE_FORMAT_STRING).startOf('day');

    const anchor = (anchorDate ? moment(anchorDate, DATE_FORMAT_STRING) : moment()).startOf('day');

    // Anchor lies within the available range.
    if (!anchor.isBefore(availableFrom, 'day') && !anchor.isAfter(availableTo, 'day')) {
      let from = anchor.clone();
      let to = anchor.clone().add(requestedWidth - 1, 'days');

      // Doesn't fit -> shift left while keeping anchor included.
      if (to.isAfter(availableTo, 'day')) {
        const shift = to.diff(availableTo, 'days');
        to = availableTo;
        from.subtract(shift, 'days');
      }

      return { from: from.format(DATE_FORMAT_STRING), to: to.format(DATE_FORMAT_STRING) };
    }

    // Anchor is outside the available range.
    return {
      from: availableFrom.format(DATE_FORMAT_STRING),
      to: availableFrom
        .clone()
        .add(requestedWidth - 1, 'days')
        .format(DATE_FORMAT_STRING),
    };
  }
}
